/**
 * Voice Memo Analysis Panel Component
 * Displays analysis content in secondary panel with title editing
 */

import { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SecondaryPanelContent, SecondaryPanelOpenButton } from '@/components/layout/secondary-panel-content';
import { MarkdownPreview } from '@/components/features/markdown-preview';
import type { VoiceMemo } from './types';

interface VoiceMemoAnalysisPanelProps {
  memo: VoiceMemo;
  onClose: () => void;
  onGenerateTitle?: (memoId: string) => void;
  onUpdateTitle?: (memoId: string, title: string) => void;
}

export function VoiceMemoAnalysisPanel({
  memo,
  onClose,
  onGenerateTitle,
  onUpdateTitle
}: VoiceMemoAnalysisPanelProps) {
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(memo.title || '');

  // Check if transcript has workflow tracking indicating it's been analyzed
  const hasAnalysis = memo.transcript?.content?.includes('current_stage: reviewed') ||
                      memo.transcript?.content?.includes('workflow:') ||
                      memo.status === 'analyzed';

  const transcriptContent = memo.transcript?.content || 'Transcript not available';

  // Extract raw transcript section if available
  const rawTranscriptMatch = transcriptContent.match(/## Raw Transcript\s*\n([\s\S]*?)(?=\n##|$)/);
  const rawTranscript = rawTranscriptMatch ? rawTranscriptMatch[1].trim() : '';

  const handleGenerateTitle = async () => {
    if (!onGenerateTitle) return;

    setIsGeneratingTitle(true);
    try {
      await onGenerateTitle(memo.id);
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  const handleSaveTitle = () => {
    if (onUpdateTitle && titleValue.trim()) {
      onUpdateTitle(memo.id, titleValue.trim());
      setEditingTitle(false);
    }
  };

  return (
    <SecondaryPanelContent
      title={`Analysis: ${memo.title || memo.filename.replace(/\.(m4a|wav|m4b)$/, '')}`}
      onClose={onClose}
      headerActions={
        memo.transcript?.path ? (
          <SecondaryPanelOpenButton
            onClick={() => {
              console.log('Opening analysis in editor:', memo.transcript?.path);
              // In real implementation, this would open the analysis document
            }}
          />
        ) : undefined
      }
    >
      <div className="p-6 space-y-6">
        {/* Title Section */}
        <div className="border-b border-border pb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Title
            </label>
            {!editingTitle && !memo.title && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleGenerateTitle}
                disabled={isGeneratingTitle || !memo.transcript?.content}
                className="h-7 text-xs"
              >
                {isGeneratingTitle ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-3 h-3 mr-1.5" />
                    Generate Title
                  </>
                )}
              </Button>
            )}
          </div>

          {editingTitle ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter title..."
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveTitle();
                  } else if (e.key === 'Escape') {
                    setEditingTitle(false);
                    setTitleValue(memo.title || '');
                  }
                }}
              />
              <Button
                size="sm"
                variant="default"
                onClick={handleSaveTitle}
                className="h-7"
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingTitle(false);
                  setTitleValue(memo.title || '');
                }}
                className="h-7"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div
              className="flex items-center gap-2 group cursor-pointer"
              onClick={() => setEditingTitle(true)}
            >
              {memo.title ? (
                <h3 className="text-base font-semibold text-foreground flex-1">
                  {memo.title}
                </h3>
              ) : (
                <p className="text-sm text-muted-foreground italic flex-1">
                  No title (click to edit or generate)
                </p>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingTitle(true);
                }}
              >
                <FileText className="w-3 h-3" />
              </Button>
            </div>
          )}

          {memo.title && (
            <p className="text-xs text-muted-foreground mt-1">
              Filename: {memo.filename}
            </p>
          )}
        </div>

        {hasAnalysis ? (
          <>
            {/* Show full transcript with analysis */}
            <div
              style={{
                animation: 'slideDownStagger 0.4s ease-out 0s both'
              }}
            >
              <MarkdownPreview content={transcriptContent} className="max-w-none" />
            </div>

            {/* Show raw transcript section separately if available */}
            {rawTranscript && (
              <div className="mt-6 pt-6 border-t border-border">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Raw Transcript
                </h3>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{rawTranscript}</p>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">Analysis not available.</p>
            <p className="text-sm text-muted-foreground">Generate analysis to view content insights, topics, and action items.</p>
          </div>
        )}
      </div>
    </SecondaryPanelContent>
  );
}
